/**
 * The window of time the payments list is looking at.
 *
 * The endpoint takes `from` and `to` as plain days, so that is what these
 * produce. A range is worked out when it is asked for rather than stored: a
 * console left open overnight would otherwise go on asking about yesterday's
 * idea of "last month".
 */

/** `YYYY-MM-DD` from a local date, built by hand rather than through
 *  `toISOString`, which converts to UTC first and can hand back the day
 *  before for anybody east of Greenwich. */
const asDay = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

/** `today`, less some number of months. Rolls the year back on its own, and
 *  clamps the way `Date` does — a month back from the 31st lands on the last
 *  day of a shorter month rather than overshooting into the next one. */
const monthsAgo = (count: number): Date => {
  const date = new Date();
  const day = date.getDate();
  date.setMonth(date.getMonth() - count);
  if (date.getDate() !== day) date.setDate(0);
  return date;
};

export type DateRangeId =
  | "last_month"
  | "last_3_months"
  | "last_6_months"
  | "this_year"
  | "all";

/**
 * What the range dropdown offers.
 *
 * `all` is last because it is the way out of a filter rather than another
 * filter — and it is the one the control treats as "nothing applied", so
 * picking it puts the box back to reading "Date range".
 */
export const DATE_RANGE_OPTIONS: { value: DateRangeId; label: string }[] = [
  { value: "last_month", label: "Last month" },
  { value: "last_3_months", label: "Last 3 months" },
  { value: "last_6_months", label: "Last 6 months" },
  { value: "this_year", label: "This year" },
  { value: "all", label: "All time" },
];

/** The range the list opens on. A month is the window a revenue screen is
 *  usually being asked about, and it is what the design shows selected. */
export const DEFAULT_DATE_RANGE: DateRangeId = "last_month";

/** The one option that means "no window at all" — see `FilterDropdown`, which
 *  shows its placeholder while this is what is selected. */
export const NO_DATE_RANGE: DateRangeId = "all";

/**
 * The two days a range travels as, or nothing at all for "All time".
 *
 * "Last month" is the month up to today rather than the previous calendar
 * month: the label reads as a stretch of time, and a screen opened on the 2nd
 * that showed only the 1st to the 31st of last month would look empty for a
 * reason nobody could see.
 */
export const dateRangeFor = (
  id: DateRangeId
): { from?: string; to?: string } => {
  if (id === "all") return {};

  const to = asDay(new Date());

  if (id === "this_year") {
    const january = new Date(new Date().getFullYear(), 0, 1);
    return { from: asDay(january), to };
  }

  const months = id === "last_6_months" ? 6 : id === "last_3_months" ? 3 : 1;
  return { from: asDay(monthsAgo(months)), to };
};
